/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Tooltip, Button, Col, Input, Select, Modal } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";

import api from "../../services/api";

import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
// eslint-disable-next-line import/extensions
import Table from "../../components/Table";
import CustomerModal from "./CustomerModal";
import { Container } from "./styles";
import UserRoles from "../../enums/UserRoles";
import { IContractor } from "../Contractors";
import { ICustomersHoursFactory } from "../Dashboards/CustomersHoursFactory";

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

export interface ICustomer {
  id: string;
  name: string;
  document_number: string;
  generate_purchase_order: boolean;
  cep: string;
  neighborhood: string;
  city: string;
  uf: string;
  street: string;
  number: string;
  contractors: IContractor[];
  hoursFactory?: ICustomersHoursFactory;
}

const Customers: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editedCustomer, setEditedCustomer] = useState<ICustomer | undefined>();

  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation(["customers"]);

  // Filters
  const [name, setName] = useState<string | undefined>();
  const [nameValue, setNameValue] = useState<string | undefined>("");
  const [documentNumber, setDocumentNumber] = useState<string | undefined>();
  const [documentNumberValue, setDocumentNumberValue] = useState<
    string | undefined
  >("");
  const [generatePurchaseOrder, setGeneratePurchaseOrder] = useState<
    boolean | undefined
  >();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role],
  );

  const searchParams = useMemo(() => {
    return {
      name,
      document_number: documentNumber,
      generate_purchase_order: generatePurchaseOrder,
    };
  }, [name, documentNumber, generatePurchaseOrder]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined, isDeleting?: boolean) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 401:
          description = "Token inválido ou faltando";
          break;
        case 404:
          description = "O cliente não foi encontrado";
          break;
        default:
          description = isDeleting
            ? "Ocorreu um erro ao excluir o cliente, tente novamente"
            : "Ocorreu um erro ao buscar os clientes, tente novamente";
      }

      addToast({
        type: "error",
        title: isDeleting ? "Erro na exclusão" : "Erro na busca",
        description,
      });
    },
    [addToast],
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<ICustomer[]>("/customers", {
        params: searchParams,
      })
      .then(response => {
        setCustomers(response.data);
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
  }, [searchParams]); // do not include generateErrorToast

  const handleDeleteCustomer = useCallback(
    (customerId: string) => {
      api
        .delete(`/customers/${customerId}`)
        .then(() => {
          addToast({
            type: "success",
            title: "Cliente excluido",
            description: "O cliente foi excluido com sucesso",
          });

          setCustomers(oldCustomers => [
            ...oldCustomers.filter(cust => cust.id !== customerId),
          ]);
        })
        .catch(err => {
          if (axios.isAxiosError(err)) {
            const serverError = err as AxiosError;
            generateErrorToast(serverError.response, true);
          }
        });
    },
    [addToast, generateErrorToast],
  );

  const handleEditCustomer = useCallback((customer: ICustomer) => {
    setEditedCustomer(customer);
    setModalVisible(true);
  }, []);

  const showDelConfirm = useCallback(
    (customerId, customerName) => {
      confirm({
        title: t("deleteModal.title"),
        icon: <ExclamationCircleOutlined />,
        content: t("deleteModal.alert", { name: customerName }),
        centered: true,
        okText: t("deleteModal.yes"),
        cancelText: t("deleteModal.no"),
        okButtonProps: { type: "primary", danger: true },
        onOk: () => handleDeleteCustomer(customerId),
      });
    },
    [t, handleDeleteCustomer],
  );

  const columns: ColumnsType<ICustomer> = [
    {
      title: t("name"),
      dataIndex: "name",
    },
    {
      title: t("document_number"),
      dataIndex: "document_number",
    },
    {
      title: t("generate_purchase_order"),
      dataIndex: "generate_purchase_order",
      render: generate => (
        <Badge size="default" status={generate ? "success" : "error"} />
      ),
    },
    {
      title: "Ações",
      dataIndex: "actions",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEditCustomer(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            type="link"
            danger
            onClick={() => showDelConfirm(record.id, record.name)}
          />
        </>
      ),
    },
  ];

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setName(undefined);
        setDocumentNumber(undefined);
        setGeneratePurchaseOrder(undefined);
      }
    },
    [showFilters],
  );

  const handleCloseModal = useCallback(
    (customer?: ICustomer) => {
      setEditedCustomer(undefined);
      if (customer) {
        const index = customers.findIndex(usr => usr.id === customer.id);
        const newCustomers = customers;

        if (index > -1) {
          newCustomers[index] = customer;
        } else {
          newCustomers.push(customer);
        }
        setCustomers([...newCustomers]);
      }
      setModalVisible(false);
    },
    [customers],
  );

  const extraActions = useMemo(
    () =>
      isLoggedUserManager ? (
        <Tooltip title={t("actions.create")}>
          <Button
            icon={<PlusCircleOutlined />}
            type="link"
            size="large"
            onClick={() => setModalVisible(true)}
          />
        </Tooltip>
      ) : (
        <></>
      ),
    [isLoggedUserManager, t],
  );

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={9} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("name")}</Label>
            </div>
            <Search
              placeholder={t("name")}
              onSearch={value => {
                setName(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setNameValue(value || undefined);
              }}
              value={nameValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={9} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("document_number")}</Label>
            </div>
            <Search
              placeholder={t("document_number")}
              onSearch={value => {
                setDocumentNumber(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setDocumentNumberValue(value || undefined);
              }}
              value={documentNumberValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={6} xs={24}>
            <div>
              <Label color="#575962">{t("generate_purchase_order")}</Label>
            </div>
            <Select
              placeholder={t("generate_purchase_order")}
              style={{ width: "100%" }}
              allowClear
              onChange={value => setGeneratePurchaseOrder(value)}
            >
              <Option value="true">{t("purchase_order.TRUE.label")}</Option>
              <Option value="false">{t("purchase_order.FALSE.label")}</Option>
            </Select>
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [documentNumberValue, nameValue, showFilters, t],
  );

  return (
    <Container>
      <CustomerModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        customer={editedCustomer}
      />
      <Table
        data={customers}
        columns={columns}
        title={
          <>
            Clientes
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        extraActions={extraActions}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={extraFilters}
      />
    </Container>
  );
};

export default Customers;
